import React, {
  createContext,
  FunctionComponent,
  useContext,
  useState
} from 'react';

import useTags from './useTags';

import {
  emptyFilterEntry,
  initialCategoryFilterValues,
  initialYearFilterValues
} from '../constants/filter';

import {
  FilterListEntry,
  YearFilterValues,
  CategoryFilterValues,
  TagsFilterValues
} from '../../../shared/types/filter';
import {Tag} from '../../../shared/types/work';

interface FiltersContextType {
  filterIsOpen: boolean;
  selectedYearFilterOptions: Array<FilterListEntry<YearFilterValues>>;
  setSelectedYears: (
    selectedYearFilterOptions: FilterListEntry<YearFilterValues>
  ) => void;
  currentYearFilterValues: Array<YearFilterValues>;
  selectedCategoryFilterOptions: Array<FilterListEntry<CategoryFilterValues>>;
  setSelectedCategories: (
    selectedCategoryFilterOptions: FilterListEntry<CategoryFilterValues>
  ) => void;
  currentCategoryFilterValues: Array<CategoryFilterValues>;
  toggleFilterPanel: () => void;
  selectedTagFilterOptions: Array<FilterListEntry<Tag>>;
  setSelectedTags: (selectedTagsFilterOptions: FilterListEntry<Tag>) => void;
  currentTagFilterValues: TagsFilterValues;
}

export const FiltersContext = createContext<FiltersContextType>({
  filterIsOpen: false,
  setSelectedYears: (
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    selectedYearFilterOptions: FilterListEntry<YearFilterValues>
  ) => {},
  currentYearFilterValues: initialYearFilterValues.map(
    yearEntry => yearEntry.value
  ),
  setSelectedCategories: (
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    selectedCategoryFilterOptions: FilterListEntry<CategoryFilterValues>
  ) => {},
  selectedYearFilterOptions: initialYearFilterValues,
  selectedCategoryFilterOptions: initialCategoryFilterValues,
  toggleFilterPanel: () => {},
  currentCategoryFilterValues: initialCategoryFilterValues.map(
    categoryEntry => categoryEntry.value
  ),
  selectedTagFilterOptions: [emptyFilterEntry],
  setSelectedTags: (
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    selectedTagsFilterOptions: FilterListEntry<Tag>
  ) => {},
  currentTagFilterValues: ['']
});

/**
 * The filters context provider
 * Returns the selected filter values
 * Returns the filter panel opening state
 */
const FiltersProvider: FunctionComponent = props => {
  const {children} = props;
  const {tagsFilterEntries} = useTags();

  /**
   *  State and function to hold and change the year filters
   */

  // State of all selected year filter options
  const [selectedYearFilterOptions, setSelectedYearFilterOptions] = useState<
    Array<FilterListEntry<YearFilterValues>>
  >(initialYearFilterValues);

  // Current year filter values
  const currentYearFilterValues = selectedYearFilterOptions.map(
    selectedYearFilter => selectedYearFilter.value
  );

  // Function to set the selected years filter state
  const setSelectedYears = (
    selectedYear: FilterListEntry<YearFilterValues>
  ) => {
    const yearIsAlreadySelected =
      selectedYearFilterOptions.includes(selectedYear);

    if (yearIsAlreadySelected) {
      setSelectedYearFilterOptions(prevState =>
        prevState.filter(option => option !== selectedYear)
      );

      return;
    }

    setSelectedYearFilterOptions(prevState => prevState.concat(selectedYear));
  };

  /**
   *  State and function to hold and change the category filters
   */

  // State of all selected category filter options
  const [selectedCategoryFilterOptions, setSelectedCategoryFilterOption] =
    useState<Array<FilterListEntry<CategoryFilterValues>>>(
      initialCategoryFilterValues
    );

  // Current category values
  const currentCategoryFilterValues = selectedCategoryFilterOptions.map(
    selectedCategoryFilterEntry => selectedCategoryFilterEntry.value
  ) as Array<CategoryFilterValues>;

  // Function to set the selected categories filter state
  const setSelectedCategories = (
    selectedCategory: FilterListEntry<CategoryFilterValues>
  ) => {
    const categoryIsAlreadySelected =
      selectedCategoryFilterOptions.includes(selectedCategory);

    if (categoryIsAlreadySelected) {
      setSelectedCategoryFilterOption(prevState =>
        prevState.filter(option => option.value !== selectedCategory.value)
      );

      return;
    }

    setSelectedCategoryFilterOption(prevState =>
      prevState.concat(selectedCategory)
    );
  };

  /**
   * State and function to hold and change the tags filter
   */

  // State of all tags that were selected
  const [selectedTagFilterOptions, setSelectedTagFilterOptions] = useState<
    Array<FilterListEntry<Tag>>
  >([]);

  // Current tag filter values
  const currentTagFilterValues = selectedTagFilterOptions.map(
    selectedTagFilterEntry => selectedTagFilterEntry.value
  ) as TagsFilterValues;

  // Function to set the selected tags filter state
  const setSelectedTags = (selectedTag: FilterListEntry<Tag>) => {
    const tagIsAlreadySelected = currentTagFilterValues.includes(
      selectedTag.value
    );

    // Empty selected tags when all existing tags are selected
    if (selectedTagFilterOptions.length === tagsFilterEntries.length) {
      setSelectedTagFilterOptions([]);

      if (selectedTag) {
        setSelectedTagFilterOptions([selectedTag]);
      }
      return;
    }

    if (tagIsAlreadySelected) {
      setSelectedTagFilterOptions(prevState =>
        prevState.filter(option => option.value !== selectedTag.value)
      );

      return;
    }

    setSelectedTagFilterOptions(prevState => prevState.concat(selectedTag));
  };

  /**
   *  Function to toggle the filter panel
   */
  const [filterIsOpen, setFilterIsOpen] = useState(false);

  const toggleFilterPanel = () => {
    setFilterIsOpen(prevState => !prevState);
  };

  // All values to be returned by the hook
  const value = {
    selectedYearFilterOptions,
    setSelectedYears,
    currentYearFilterValues,
    selectedCategoryFilterOptions,
    setSelectedCategories,
    currentCategoryFilterValues,
    filterIsOpen,
    toggleFilterPanel,
    selectedTagFilterOptions,
    setSelectedTags,
    currentTagFilterValues
  };

  return (
    <FiltersContext.Provider value={value}>{children}</FiltersContext.Provider>
  );
};

export default FiltersProvider;

/**
 * Hook to get the filters context
 */
export const useFilters: () => FiltersContextType = () =>
  useContext(FiltersContext);
